/**
 * Server-agnostic output binder — the TS twin of ISM's
 * FormDocumentOutputRenderService::renderForResult.
 *
 * Turns a FormDocumentOutput's template content into a "rendered" document:
 * the same EacoDocumentContent shape, but with `data.bound*` baked into the
 * output-only element types (form-results / score / event-timeline /
 * form-field / form-status / form-signoff) plus `{{form.*}}` placeholder
 * substitution on headings/paragraphs. The @eaco/forms-ui-* renderers read
 * `element.data.bound*` directly.
 *
 * Pure: it takes already-fetched inputs (the template, the answer-version
 * content, and the result's persisted fields) and returns bound content — no
 * DB, no network. Callers (forms-pdf-service) do the fetching.
 *
 * Actor display names are an input (nullable) rather than resolved here — the
 * caller supplies them if it can; today they degrade to the filler email / null.
 */
import type {
  AnswerGroupMarker,
  AnswerListItem,
  BoundLinkedField,
  DocumentElement,
  EacoDocumentContent,
  FormOutputContext,
  LogicRule,
  RenderedOutputContent,
  ResolvedElementState,
  GeoPlace,
  BoundGeoAnswer,
  BoundAnswerPick,
  ScoreBreakdownSnapshot,
} from "./types";
import { isAnswerGroupMarker } from "./types";
import { forEachVisibleLeaf, isRepeatScope } from "./logic/visibility";
import type { VisibleAncestor } from "./logic/visibility";
import { countryByCode } from "./countries";
import { scalarScoringConfig, scoreScalarAnswer } from "./scalarScoring";
import { bindJobFieldForJob } from "./renderEntityReport";
import {
  bindDetailsTable,
  bindFormField,
  bindLinkedEventField,
  bindLinkedJobField,
  bindLinkedPropertyField,
  linkedFieldHiddenWhenEmpty,
  bindScoreBreakdown,
  parseScoreBreakdown,
} from "./outputContext";
import {
  SIGNOFF_STATUSES,
  resolveSignoffAcceptLabel,
  resolveSignoffDeclineLabel,
} from "./signoff";

const SIGNOFF_SATISFACTORY = SIGNOFF_STATUSES.find(
  (s) => s.id === "satisfactory",
)!.value; // 2
const SIGNOFF_UNSATISFACTORY = SIGNOFF_STATUSES.find(
  (s) => s.id === "unsatisfactory",
)!.value; // 3

/** The result's persisted fields the binder reads. Timestamps are ISO strings. */
export interface FormOutputResultData {
  id: string | number;
  responses: Record<string, unknown>;
  score: number | null;
  scoreMax: number | null;
  scorePercent: number | null;
  flaggedAnswerCount: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  submittedAt: string | null;
  fillerEmail: string | null;
  /** Filler display name; null degrades to fillerEmail (graph-name resolution TBD). */
  submittedByName: string | null;
  /** The answer version's sequential number, for the form-field "version" row. */
  versionNumber: number | null;
  signoffStatus: number | null;
  signedOffAt: string | null;
  signedOffByName: string | null;
  /** JSON string or parsed object of `{ overall, ... }` sign-off comments. */
  signoffComments: string | Record<string, unknown> | null;
  /** Form's configured positive decision label; null → default "Satisfactory". */
  signoffAcceptLabel?: string | null;
  /** Form's configured negative decision label; null → default "Unsatisfactory". */
  signoffDeclineLabel?: string | null;
  /** Form's configured reviewer title (role/party); null when unset. */
  signoffReviewerTitle?: string | null;
  /**
   * The decision label snapshotted onto the result when the verdict was
   * recorded — the source of truth for display. Preferred over resolving from
   * the form's current accept/decline labels (which may have changed since).
   * Null for pre-snapshot rows → fall back to accept/decline labels.
   */
  signoffOutcomeLabel?: string | null;
  /**
   * Per-response pick counts ISM's scorer stored at save
   * (score_breakdown_json), as the JSON string or parsed. Null for results
   * saved before the breakdown existed and not yet backfilled.
   */
  scoreBreakdown?: string | ScoreBreakdownSnapshot | null;
}

export interface RenderFormOutputInput {
  outputId: string | number;
  formName: string;
  /** Active version content of the chosen output (the template). */
  outputContent: EacoDocumentContent;
  /** Version content the filler answered against (for the answer list). */
  answerContent: EacoDocumentContent | null;
  /** Effective score thresholds (form custom or defaults) — passed through to Score. */
  scoreThresholds: unknown[];
  result: FormOutputResultData;
  /**
   * Global (client-level) response sets referenced by the answered version,
   * keyed by id — em_document_response_set rows. Used to tell whether an
   * answer is flagged when the stored pick carries no snapshot.
   */
  globalResponseSets?: Record<string, { responses?: unknown[] } | null | undefined>;
  /**
   * Static map image for a geo-tag answer (the host owns the provider and
   * key). Return null to render the place text alone.
   */
  staticMapUrl?: (lat: number, lng: number) => string | null;
  /**
   * IANA zone every instant (started / submitted / timeline / sign-off) is
   * shown in: the filler's own zone, else the form owner's. Null renders in
   * whatever zone the renderer runs in (a browser preview).
   */
  timeZone?: string | null;
  /**
   * The job and managed property the result was filled against, for the
   * `job-field` and `details-table` elements. Null / absent renders those
   * elements empty.
   */
  context?: FormOutputContext | null;
}

/** A zone name Intl accepts; anything else is treated as "no zone". */
export function isValidTimeZone(tz: string | null | undefined): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat("en-AU", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * Format an ISO instant for people, in a zone. Naive strings (no offset)
 * are read as UTC, which is how every result timestamp is stored. Falls back
 * to the runtime's zone when `timeZone` is missing or unknown, and to the raw
 * string when it isn't a date at all.
 */
export function formatInstant(
  iso: string | null | undefined,
  timeZone: string | null | undefined,
  style: "datetime" | "date" = "datetime",
): string {
  if (!iso) return "";
  const s = String(iso);
  const naive = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?$/.test(s);
  const d = new Date(naive ? `${s.replace(" ", "T")}Z` : s);
  if (Number.isNaN(d.getTime())) return s;
  const opts: Intl.DateTimeFormatOptions =
    style === "date"
      ? { day: "numeric", month: "short", year: "numeric" }
      : { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" };
  if (isValidTimeZone(timeZone)) opts.timeZone = timeZone;
  return new Intl.DateTimeFormat("en-AU", opts).format(d);
}

interface Bindings {
  answers: AnswerListItem[];
  score: Record<string, unknown>;
  events: EventRow[];
  formFields: Record<string, FieldEntry>;
  /**
   * Raw ISO values for the date-valued form fields, so a `form-field`
   * element can re-format them to its own chosen format. `formFields`
   * above holds the default rendering, which is what `{{form.*}}` prints.
   */
  formFieldInstants: Record<string, string | null>;
  formStatus: { state: string; label: string };
  formSignoff: Record<string, unknown>;
  placeholders: Record<string, string>;
  context: FormOutputContext | null;
  scoreBreakdown: ScoreBreakdownSnapshot | null;
  /** Zone job dates are shown in, like the result's own instants. */
  timeZone: string | null;
}

interface AnswerRow {
  elementId: string;
  label: string;
  typeId: string;
  formattedValue: string;
  raw: unknown;
  flagged: boolean;
  score: number | null;
  files?: { fileId: number; name: string | null }[];
  filesAreImages?: boolean;
  /** Only when `withGroups` is asked for; see SubmittedAnswerGroup. */
  group?: SubmittedAnswerGroup;
}

// Answer types whose value carries uploaded-file references (`{ fileId }`).
export const IMAGE_FILE_TYPES = new Set(["image-upload", "signature"]);

/** Options for controlling what markers are emitted by collectAnswers. */
export interface CollectAnswersOptions {
  /** Form-local response sets (answerContent.responseSets), for the flag fallback. */
  localSets?: unknown[];
  /** Global response sets by id (caller-supplied), for the flag fallback. */
  globalSets?: Record<string, { responses?: unknown[] } | null | undefined>;
  /** Static map builder for geo-tag answers (see RenderFormOutputInput.staticMapUrl). */
  staticMapUrl?: (lat: number, lng: number) => string | null;
  showPageHeadings?: boolean;
  showSectionHeadings?: boolean;
  showHeadingElements?: boolean;
  /**
   * Tag every row with where it sits (page / heading / section / repeat
   * instance) instead of emitting the standalone markers above. Off by
   * default so the output renderer's rows are untouched.
   */
  withGroups?: boolean;
  /**
   * The document's logic rules; the visibility walk re-resolves them per
   * repeat instance against the top-level answers overlaid with the
   * instance's own, as the fill renderer's RepeatScopeProvider does.
   */
  logicRules?: LogicRule[];
}
export const GENERIC_FILE_TYPES = new Set(["file-upload"]);

/**
 * Pull `{ fileId, name }` refs out of an image / signature / file answer
 * value. Exported so the media collector reads an answer's files exactly
 * as the output renderer does — one parser, so a zip of a response and
 * the report of it cannot disagree about what it contains.
 */
export function extractFiles(
  typeId: string,
  rawValue: unknown,
): { fileId: number; name: string | null }[] | undefined {
  if (!IMAGE_FILE_TYPES.has(typeId) && !GENERIC_FILE_TYPES.has(typeId)) {
    return undefined;
  }
  const items = Array.isArray(rawValue)
    ? rawValue
    : rawValue != null
      ? [rawValue]
      : [];
  const files: { fileId: number; name: string | null }[] = [];
  for (const it of items) {
    if (!it || typeof it !== "object") continue;
    const rawFid = (it as { fileId?: unknown }).fileId;
    const fid =
      typeof rawFid === "number"
        ? rawFid
        : typeof rawFid === "string" && /^\d+$/.test(rawFid)
          ? Number(rawFid)
          : null;
    if (fid == null || fid <= 0) continue;
    const n = (it as { name?: unknown }).name;
    files.push({ fileId: fid, name: typeof n === "string" ? n : null });
  }
  return files.length ? files : undefined;
}

interface EventRow {
  at: string;
  type: string;
  label: string;
  actor: string | null;
}

interface FieldEntry {
  fieldId: string;
  label: string;
  formattedValue: string;
}

/** Bind a result through an output template → self-contained rendered content. */
export function renderFormOutput(
  input: RenderFormOutputInput,
): RenderedOutputContent {
  const content: EacoDocumentContent = {
    elements: Array.isArray(input.outputContent?.elements)
      ? input.outputContent.elements
      : [],
    logic: input.outputContent?.logic,
    responseSets: input.outputContent?.responseSets,
  };

  const bindings = buildBindings(input);
  const bound: RenderedOutputContent = {
    ...content,
    elements: walkElements(hoistPageBreaks(content.elements), bindings),
    __binding: {
      resultId: String(input.result.id),
      outputId: String(input.outputId),
      formName: input.formName,
      timeZone: isValidTimeZone(input.timeZone) ? input.timeZone : null,
    },
  };
  return bound;
}

/**
 * A container a hoisted page break sat under, as `data.hoistedFrom` records
 * it: its id (for the logic state the renderer resolves) and its static
 * hidden flag (the fallback when no rule fires for it).
 */
export interface HoistedAncestor {
  id: string;
  hidden: boolean;
}

/**
 * One page break a Columns block held, as the single break that replaces
 * them all records it: its own id and static flag, plus the containers
 * between the Columns block and it, outermost first.
 */
export interface HoistedBreakCandidate extends HoistedAncestor {
  ancestors: HoistedAncestor[];
}

/**
 * Page breaks placed inside a Columns block move to just after it: print
 * engines ignore forced breaks inside the columns' grid (gotenberg's
 * Chromium prints both sides of such a break on one sheet), so the break
 * lands where it can take effect. A block's breaks become ONE break after
 * it (several would each start a sheet, leaving blank ones), the first in
 * document order, carrying every break it stands for as
 * `data.hoistedFrom`: each one's own id and static flag and the containers
 * it sat under. The renderer shows it while any of them would have shown
 * in place (isHoistedBreakHidden); whether a container is hidden, or a
 * statically hidden break is shown, can turn on a logic rule that only
 * resolves at render time, so nothing is decided here. Other containers
 * keep their breaks.
 */
export function hoistPageBreaks(elements: DocumentElement[]): DocumentElement[] {
  const ancestorOf = (el: DocumentElement): HoistedAncestor => ({
    id: String(el.id ?? ""),
    hidden: (el.data as { hidden?: unknown } | undefined)?.hidden === true,
  });
  const out: DocumentElement[] = [];
  for (const element of elements) {
    if (!element || typeof element !== "object") continue;
    if (element.typeId === "columns" && Array.isArray(element.elements)) {
      let first: DocumentElement | null = null;
      const candidates: HoistedBreakCandidate[] = [];
      const strip = (els: DocumentElement[], ancestors: HoistedAncestor[]): DocumentElement[] =>
        els.filter((child) => {
          if (!child) return true;
          if (child.typeId === "page-break") {
            if (!first) first = child;
            candidates.push({ ...ancestorOf(child), ancestors });
            return false;
          }
          if (Array.isArray(child.elements)) {
            child.elements = strip(child.elements, [...ancestors, ancestorOf(child)]);
          }
          return true;
        });
      const copy = JSON.parse(JSON.stringify(element)) as DocumentElement;
      copy.elements = strip(copy.elements ?? [], [ancestorOf(element)]);
      out.push(copy);
      if (first) {
        const lead = first as DocumentElement;
        out.push({
          ...lead,
          column: undefined,
          data: { ...(lead.data ?? {}), hoistedFrom: candidates },
        });
      }
      continue;
    }
    out.push(
      Array.isArray(element.elements)
        ? { ...element, elements: hoistPageBreaks(element.elements) }
        : element,
    );
  }
  return out;
}

/**
 * The hide decision for a page break hoistPageBreaks moved out of a Columns
 * block, replacing the renderer's usual one: hidden unless at least one of
 * the breaks it stands for would have shown in place, meaning the break
 * itself and every container it sat under is visible. For each, a fired
 * logic rule's hidden state wins, else its static flag, exactly as the
 * renderer decides for elements still in place. Undefined for an element
 * without a `hoistedFrom` record, whose usual decision stands.
 */
export function isHoistedBreakHidden(
  data: Record<string, unknown> | undefined,
  resolved: ReadonlyMap<string, ResolvedElementState>,
): boolean | undefined {
  const from = data?.hoistedFrom;
  if (!Array.isArray(from)) return undefined;
  const hidden = (node: unknown): boolean => {
    if (!node || typeof node !== "object") return false;
    const { id, hidden: staticHidden } = node as Partial<HoistedAncestor>;
    const logicHidden = id ? resolved.get(String(id))?.hidden : undefined;
    return logicHidden !== undefined ? logicHidden : staticHidden === true;
  };
  return !from.some((candidate) => {
    if (!candidate || typeof candidate !== "object") return false;
    const ancestors = (candidate as Partial<HoistedBreakCandidate>).ancestors;
    return !hidden(candidate) && !(Array.isArray(ancestors) && ancestors.some(hidden));
  });
}

/**
 * Attach a Linked Entity Data element's bound value, and honour its
 * "hide when empty" setting by raising the same static `data.hidden`
 * flag the renderers and the app lowering already read — so nothing
 * downstream needs to know why the element went away.
 */
function applyLinkedField(
  data: Record<string, unknown>,
  bound: BoundLinkedField,
): void {
  data.boundLinkedField = bound;
  if (linkedFieldHiddenWhenEmpty(data, bound)) data.hidden = true;
}

function walkElements(
  elements: DocumentElement[],
  bindings: Bindings,
): DocumentElement[] {
  const out: DocumentElement[] = [];
  for (const original of elements) {
    if (!original || typeof original !== "object") continue;
    const element: DocumentElement = { ...original };
    const data: Record<string, unknown> = { ...(element.data ?? {}) };
    element.data = data;
    const typeId = String(element.typeId ?? "");

    switch (typeId) {
      case "form-results":
        data.boundAnswers = filterAnswers(bindings.answers, data);
        break;
      case "score":
        data.boundScore = bindings.score;
        break;
      case "event-timeline":
        data.boundEvents = bindings.events;
        break;
      case "form-field":
        data.boundField = bindFormField(
          bindings.formFields,
          bindings.formFieldInstants,
          data,
          bindings.timeZone,
        );
        break;
      case "form-status":
        data.boundStatus = bindings.formStatus;
        break;
      case "form-signoff":
        data.boundSignoff = bindings.formSignoff;
        break;
      case "job-field":
        data.boundJobField = bindJobFieldForJob(
          bindings.context?.job ?? null,
          data,
          bindings.timeZone,
        );
        break;
      // The "Linked Entity Data" family — one field of an entity the
      // result was filled against. All three write the same payload
      // shape, so the app lowering treats them alike.
      case "linked-job-field":
        applyLinkedField(
          data,
          bindLinkedJobField(bindings.context, data, bindings.timeZone),
        );
        break;
      case "linked-property-field":
        applyLinkedField(
          data,
          bindLinkedPropertyField(bindings.context, data),
        );
        break;
      case "linked-event-field":
        applyLinkedField(
          data,
          bindLinkedEventField(bindings.context, data, bindings.timeZone),
        );
        break;
      case "details-table":
        data.boundDetails = bindDetailsTable(
          data,
          bindings.context,
          bindings.formFields,
          bindings.timeZone,
        );
        break;
      case "score-breakdown":
        data.boundScoreBreakdown = bindScoreBreakdown(
          data,
          bindings.scoreBreakdown,
        );
        break;
      case "heading":
      case "paragraph":
        for (const key of ["label", "subtitle", "text"] as const) {
          if (typeof data[key] === "string") {
            data[key] = substitutePlaceholders(
              data[key] as string,
              bindings.placeholders,
            );
          }
        }
        break;
    }

    if (Array.isArray(element.elements)) {
      element.elements = walkElements(element.elements, bindings);
    }
    out.push(element);
  }
  return out;
}

function buildBindings(input: RenderFormOutputInput): Bindings {
  const { result, formName } = input;
  const formFields = buildFormFieldsMap(input);
  const placeholders: Record<string, string> = { "{{form.name}}": formName };
  for (const [fieldId, field] of Object.entries(formFields)) {
    placeholders[`{{form.${fieldId}}}`] = field.formattedValue;
  }
  return {
    answers: buildAnswerList(input),
    score: {
      score: result.score,
      scoreMax: result.scoreMax,
      scorePercent: result.scorePercent,
      flaggedAnswerCount: result.flaggedAnswerCount || 0,
      thresholds: input.scoreThresholds,
    },
    events: buildEventList(result),
    formFields,
    formFieldInstants: {
      startedAt: result.createdAt ?? null,
      submittedAt: result.submittedAt ?? null,
    },
    formStatus: buildFormStatus(result),
    formSignoff: buildFormSignoff(result),
    placeholders,
    context: input.context ?? null,
    scoreBreakdown: parseScoreBreakdown(result.scoreBreakdown ?? null),
    timeZone: isValidTimeZone(input.timeZone) ? input.timeZone : null,
  };
}

function buildFormStatus(result: FormOutputResultData): { state: string; label: string } {
  return result.submittedAt
    ? { state: "submitted", label: "Submitted" }
    : { state: "in-progress", label: "In Progress" };
}

function buildFormSignoff(result: FormOutputResultData): Record<string, unknown> {
  const reviewerTitle = result.signoffReviewerTitle?.trim() || null;
  const empty = {
    verdict: null,
    label: null,
    signedOffAt: null,
    reviewerName: null,
    reviewerTitle,
    overallComment: null,
  };
  const status = result.signoffStatus;
  if (
    !result.signedOffAt ||
    (status !== SIGNOFF_SATISFACTORY && status !== SIGNOFF_UNSATISFACTORY)
  ) {
    return empty;
  }
  const isSatisfactory = status === SIGNOFF_SATISFACTORY;

  let overallComment: string | null = null;
  const raw = result.signoffComments;
  const comments =
    typeof raw === "string" ? safeJsonParse(raw) : raw && typeof raw === "object" ? raw : null;
  if (comments && typeof (comments as any).overall === "string" && (comments as any).overall !== "") {
    overallComment = (comments as any).overall;
  }

  const labelConfig = {
    signoffAcceptLabel: result.signoffAcceptLabel ?? null,
    signoffDeclineLabel: result.signoffDeclineLabel ?? null,
  };
  // Prefer the label snapshotted at sign-off; fall back to the form's current
  // accept/decline labels for pre-snapshot rows.
  const label =
    result.signoffOutcomeLabel?.trim() ||
    (isSatisfactory
      ? resolveSignoffAcceptLabel(labelConfig)
      : resolveSignoffDeclineLabel(labelConfig));
  return {
    verdict: isSatisfactory ? "satisfactory" : "unsatisfactory",
    label,
    signedOffAt: result.signedOffAt,
    reviewerName: result.signedOffByName,
    reviewerTitle,
    overallComment,
  };
}

function buildFormFieldsMap(input: RenderFormOutputInput): Record<string, FieldEntry> {
  const { result, formName } = input;
  const duration =
    result.createdAt && result.submittedAt
      ? formatDuration(
          Math.floor(
            (Date.parse(result.submittedAt) - Date.parse(result.createdAt)) / 1000,
          ),
        )
      : "—";
  const completedBy = resolveActor(result) || "—";
  const flagged = Number(result.flaggedAnswerCount || 0);

  const entries: Record<string, { label: string; formattedValue: string }> = {
    name: { label: "Form name", formattedValue: String(formName) },
    version: {
      label: "Form version",
      formattedValue: result.versionNumber !== null ? `v${result.versionNumber}` : "—",
    },
    startedAt: {
      label: "Started",
      formattedValue: result.createdAt ? formatInstant(result.createdAt, input.timeZone) : "—",
    },
    submittedAt: {
      label: "Submitted",
      formattedValue: result.submittedAt ? formatInstant(result.submittedAt, input.timeZone) : "—",
    },
    duration: { label: "Duration", formattedValue: duration },
    completedBy: { label: "Completed by", formattedValue: String(completedBy) },
    flaggedAnswerCount: { label: "Flagged answers", formattedValue: String(flagged) },
  };

  const out: Record<string, FieldEntry> = {};
  for (const [id, entry] of Object.entries(entries)) {
    out[id] = { fieldId: id, label: entry.label, formattedValue: entry.formattedValue };
  }
  return out;
}

function formatDuration(seconds: number): string {
  seconds = Math.floor(seconds);
  if (seconds <= 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remSeconds = seconds % 60;
  if (minutes < 60) return remSeconds > 0 ? `${minutes}m ${remSeconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  if (hours < 24) return remMinutes > 0 ? `${hours}h ${remMinutes}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`;
}

function buildAnswerList(input: RenderFormOutputInput): AnswerListItem[] {
  const responses = input.result.responses || {};
  const elements = input.answerContent?.elements;
  if (!Array.isArray(elements)) return [];
  const out: AnswerListItem[] = [];
  // Collect all markers; filtering happens in filterAnswers
  const options: CollectAnswersOptions = {
    showPageHeadings: true,
    showSectionHeadings: true,
    showHeadingElements: true,
    localSets: Array.isArray(input.answerContent?.responseSets)
      ? (input.answerContent!.responseSets as unknown[])
      : [],
    globalSets: input.globalResponseSets ?? {},
    staticMapUrl: input.staticMapUrl,
    logicRules: input.answerContent?.logic,
  };
  collectAnswers(elements, responses, out, options);
  return out;
}

/**
 * Where an answer sits in the form, so a flat review list can put the
 * structure back: a page strip, a heading, a section name, and which
 * instance of a repeating section the answer was given in.
 *
 * Each is the form author's own wording, not a derived one, and null
 * where the form has nothing to say — a form with no pages gives every
 * answer `page: null`, and the list simply shows no page strip.
 */
export interface SubmittedAnswerGroup {
  /** "Page 2 · Issues", or just "Page 2" for an unnamed page. */
  page: string | null;
  /**
   * The heading element in force above the answer. A heading runs until
   * the next one and stops at the end of its page.
   */
  heading: string | null;
  /** The innermost section's name; null outside a section. */
  section: string | null;
  /**
   * 1-based position within the innermost repeating section, null outside
   * one. When a plain section sits inside a repeat, `section` names the
   * inner one while this still counts the outer instances — which bathroom
   * the answer belongs to is worth more than the chip reading tidily.
   */
  repeatIndex: number | null;
  /** How many instances that repeating section holds. */
  repeatCount: number | null;
}

/** One answered element, for the sign-off "Submitted response" review list. */
export interface SubmittedAnswer {
  elementId: string;
  label: string;
  typeId: string;
  /** Formatted display value (empty string for file-only answers). */
  value: string;
  /** Flagged by the form's scoring response sets (→ red status). */
  flagged: boolean;
  score: number | null;
  /** Uploaded file refs for image / signature / file answers. */
  files?: { fileId: number; name: string | null }[];
  /** True when `files` are images/signatures (render as thumbnails). */
  filesAreImages?: boolean;
  hasFiles: boolean;
  /** Where in the form the answer was given, for grouping the list. */
  group: SubmittedAnswerGroup;
}

/**
 * Flatten a submitted result into its list of ANSWERED leaf elements — the
 * data behind the sign-off review's compact "Submitted response" list. Skips
 * layout containers, presentation-only nodes, unanswered fields, and fields
 * the filler never saw (hidden statically or by the form's logic rules,
 * re-evaluated against the submitted responses). `flagged` reflects the
 * scoring response sets. Element order follows the document.
 */
export function bindSubmittedAnswers(
  content:
    | { elements?: DocumentElement[]; logic?: LogicRule[] }
    | null
    | undefined,
  responses: Record<string, unknown> | null | undefined,
): SubmittedAnswer[] {
  const elements = content?.elements;
  if (!Array.isArray(elements)) return [];
  const out: AnswerListItem[] = [];
  // For sign-off review, don't include structural markers
  const options: CollectAnswersOptions = {
    // No standalone markers: each row carries its own page / heading /
    // section instead, so the list can decide where a header earns its
    // place rather than rendering one for every container it passed.
    showPageHeadings: false,
    showSectionHeadings: false,
    showHeadingElements: false,
    withGroups: true,
    logicRules: content?.logic,
  };
  collectAnswers(elements, responses ?? {}, out, options);
  // Filter to only actual answers (not markers) and map to SubmittedAnswer
  return out
    .filter((item): item is AnswerRow => !isAnswerGroupMarker(item))
    .map((r) => ({
      elementId: r.elementId,
      label: r.label,
      typeId: r.typeId,
      value: r.formattedValue,
      flagged: r.flagged,
      score: r.score,
      files: r.files,
      filesAreImages: r.filesAreImages,
      hasFiles: (r.files?.length ?? 0) > 0,
      group: r.group ?? EMPTY_ANSWER_GROUP,
    }))
    .filter((r) => r.value !== "" || r.hasFiles);
}

const EMPTY_ANSWER_GROUP: SubmittedAnswerGroup = {
  page: null,
  heading: null,
  section: null,
  repeatIndex: null,
  repeatCount: null,
};

/**
 * Presentation types that don't produce answers (heading handled
 * separately). Mirrors FormDocumentOutputRenderService::collectAnswers in
 * ISM.
 */
const PRESENTATION_TYPES = new Set([
  "paragraph",
  "divider",
  "spacer",
  "image",
  "logo",
  "rich-text",
  "card",
  "table",
  "page-break",
]);

/**
 * Colour bands is a designer widget the catalogue marks not answerable, but
 * the filler can still edit it: a saved band list is an answer worth
 * showing, an empty one is not a question that went unanswered.
 */
const isUnansweredColourBands = (typeId: string, value: unknown): boolean =>
  typeId === "colour-bands" && (!Array.isArray(value) || value.length === 0);

const WALL_DATE: Intl.DateTimeFormatOptions = { day: "numeric", month: "long", year: "numeric" };
const WALL_TIME: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" };

/**
 * A stored date, time or date-time is the filler's wall clock with no zone
 * ("2026-10-08", "08:30", "2026-10-08T09:30"), so it is shown as written,
 * never shifted. Same output as the web read-only renderers.
 */
function formatWallDate(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) return raw;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  if (Number.isNaN(d.getTime())) return raw;
  return new Intl.DateTimeFormat("en-AU", { ...WALL_DATE, timeZone: "UTC" }).format(d);
}
function formatWallTime(raw: string): string {
  const m = /^(\d{2}):(\d{2})/.exec(raw);
  if (!m) return raw;
  const d = new Date(Date.UTC(2000, 0, 1, Number(m[1]), Number(m[2])));
  if (Number.isNaN(d.getTime())) return raw;
  return new Intl.DateTimeFormat("en-AU", { ...WALL_TIME, timeZone: "UTC" }).format(d);
}
function formatWallDateTime(raw: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(raw);
  if (!m) return raw;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5])));
  if (Number.isNaN(d.getTime())) return raw;
  return new Intl.DateTimeFormat("en-AU", { ...WALL_DATE, ...WALL_TIME, timeZone: "UTC" }).format(d);
}

const str = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

/**
 * The same decimal-places parse as the currency renderer: blank means
 * unset, fractions floor, and the result is capped at Intl's 20 so a bad
 * setting can never throw inside the output binder.
 */
const parseNonNegativeInt = (v: unknown): number | undefined => {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return Math.min(Math.floor(n), 20);
};

/**
 * The answer as the filler saw it, per element type, matching the web
 * read-only renderers: wall-clock dates and times, currency with its symbol
 * and code, every name part the field shows, the country's name, a toggle
 * group's option label. Everything else falls through to the generic
 * formatter.
 */
export function formatAnswerForType(
  typeId: string,
  value: unknown,
  data: Record<string, unknown> = {},
): string {
  if (value === null || value === undefined || value === "") return "";
  switch (typeId) {
    case "date-picker":
      return typeof value === "string" ? formatWallDate(value) : formatAnswerValue(value);
    case "time-picker":
      return typeof value === "string" ? formatWallTime(value) : formatAnswerValue(value);
    case "datetime-picker":
      return typeof value === "string" ? formatWallDateTime(value) : formatAnswerValue(value);
    case "date-range": {
      if (!value || typeof value !== "object") return formatAnswerValue(value);
      const v = value as { from?: unknown; to?: unknown };
      const from = typeof v.from === "string" && v.from ? formatWallDate(v.from) : "";
      const to = typeof v.to === "string" && v.to ? formatWallDate(v.to) : "";
      if (!from && !to) return "";
      // An open end keeps its place, as the read-only renderer shows it.
      return `${from || "—"} – ${to || "—"}`;
    }
    case "currency": {
      const n = Number(value);
      if (!Number.isFinite(n)) return formatAnswerValue(value);
      const decimals = parseNonNegativeInt(data.decimalPlaces) ?? 2;
      const symbol = str(data.currencySymbol) || "$";
      const code = str(data.currencyCode) || "AUD";
      const amount = n.toLocaleString("en-AU", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      return `${symbol}${amount}${code ? ` ${code}` : ""}`;
    }
    case "name-input": {
      if (!value || typeof value !== "object") return formatAnswerValue(value);
      const v = value as Record<string, unknown>;
      const parts = [
        data.showPrefix === true ? str(v.prefix) : "",
        str(v.firstName),
        data.showMiddleName === true ? str(v.middleName) : "",
        str(v.lastName),
        data.showSuffix === true ? str(v.suffix) : "",
      ].filter((p) => p.length > 0);
      return parts.length ? parts.join(" ") : formatAnswerValue(value);
    }
    case "country": {
      if (typeof value !== "string") return formatAnswerValue(value);
      return countryByCode(value)?.name ?? value;
    }
    case "toggle-group": {
      if (typeof value !== "string") return formatAnswerValue(value);
      const options = Array.isArray(data.options) ? (data.options as Array<{ value?: unknown; label?: unknown }>) : [];
      const hit = options.find((o) => o && o.value === value);
      return hit ? str(hit.label) || value : value;
    }
    case "colour-bands": {
      if (!Array.isArray(value)) return formatAnswerValue(value);
      const unit = typeof data.unit === "string" ? data.unit : "%";
      const bands = value
        .filter((b): b is { from: number; to: number; label?: unknown } =>
          !!b && typeof b === "object" && typeof (b as { from?: unknown }).from === "number" && typeof (b as { to?: unknown }).to === "number",
        )
        .map((b) => `${b.from}${unit} – ${b.to}${unit}${str(b.label) ? ` ${str(b.label)}` : ""}`);
      return bands.length ? bands.join(", ") : formatAnswerValue(value);
    }
    default:
      return formatAnswerValue(value);
  }
}

/**
 * Mirror the fill renderer's hide decision (DocumentElement): a fired logic
 * rule's hidden state wins (so a `show` action can reveal a statically hidden
 * field), otherwise the static `data.hidden` flag applies. Hidden containers
 * hide their whole subtree, exactly as they render.
 */
/**
 * Walk the answered content the way the filler saw it (the shared
 * visibility walk) and emit answer rows in document order, with page /
 * section / heading markers where the options ask for them. Repeat scopes
 * get one marker per stored instance so the rows stay grouped, mirroring
 * the filler's per-instance headings.
 */
function collectAnswers(
  elements: DocumentElement[],
  responses: Record<string, unknown>,
  out: AnswerListItem[],
  options: CollectAnswersOptions = {},
): void {
  const repeatLabel = (element: DocumentElement, data: Record<string, unknown>): string =>
    typeof data.repeatItemName === "string" && data.repeatItemName.trim()
      ? data.repeatItemName.trim()
      : typeof data.label === "string" && data.label
        ? data.label
        : String(element.name || "Entry");

  // Grouping state for `withGroups`. Pages are numbered as they are
  // entered, so the ordinal counts the pages the filler actually saw — a
  // page a rule hid is not Page 2 to anyone.
  const pageOrdinals = new Map<string, number>();
  let pageSeq = 0;
  // The heading in force. A heading element is a sibling of the answers it
  // introduces, not their parent, so it is carried forward until the next
  // one — and dropped at a page boundary, where it stops applying.
  let currentPageId: string | null = null;
  let currentHeading: string | null = null;

  const named = (element: DocumentElement, data: Record<string, unknown>): string | null => {
    if (typeof data.label === "string" && data.label.trim()) return data.label.trim();
    if (typeof element.name === "string" && element.name.trim()) return element.name.trim();
    return null;
  };

  /**
   * Where a leaf sits, read off the containers the walk passed through.
   * Innermost wins for the section name; the repeat position comes from
   * the innermost repeat scope, which may be an outer one when a plain
   * section sits inside it.
   */
  const groupFor = (ancestors: VisibleAncestor[]): SubmittedAnswerGroup => {
    let page: string | null = null;
    let section: string | null = null;
    let repeatIndex: number | null = null;
    let repeatCount: number | null = null;
    for (const ancestor of ancestors) {
      const element = ancestor.element;
      const typeId = String(element.typeId ?? "");
      const data = (element.data && typeof element.data === "object" ? element.data : {}) as Record<string, unknown>;
      if (typeId === "page") {
        const ordinal = pageOrdinals.get(String(element.id));
        const name = typeof data.name === "string" && data.name.trim() ? data.name.trim() : null;
        page = ordinal ? (name ? `Page ${ordinal} · ${name}` : `Page ${ordinal}`) : name;
        continue;
      }
      if (isRepeatScope(element)) {
        section = named(element, data) ?? section;
        if (ancestor.instance !== undefined) {
          repeatIndex = ancestor.instance + 1;
          repeatCount = ancestor.instanceCount ?? null;
        }
        continue;
      }
      if (typeId === "section") section = named(element, data) ?? section;
    }
    return { page, heading: currentHeading, section, repeatIndex, repeatCount };
  };

  forEachVisibleLeaf(
    { elements, logic: options.logicRules },
    responses,
    ({ element, values: scope, ancestors }) => {
      const typeId = String(element.typeId ?? "");
      const id = String(element.id ?? "");
      const data = (element.data && typeof element.data === "object" ? element.data : {}) as Record<string, unknown>;

      if (options.withGroups) {
        // Done before the heading branch below, so a heading that opens a
        // new page is the new page's heading rather than being wiped by it.
        const pageId = ancestors.find((a) => String(a.element.typeId ?? "") === "page")?.element.id ?? null;
        const pageKey = pageId === null ? null : String(pageId);
        if (pageKey !== currentPageId) {
          currentPageId = pageKey;
          currentHeading = null;
        }
        if (typeId === "heading") currentHeading = named(element, data);
      }

      if (typeId === "heading") {
        if (!options.showHeadingElements) return;
        const label = typeof data.label === "string" ? data.label : String(element.name || "");
        const level = typeof data.level === "string" ? data.level : "1";
        const marker: AnswerGroupMarker = { kind: "heading", elementId: id, label, level };
        out.push(marker);
        return;
      }
      if (PRESENTATION_TYPES.has(typeId)) return;
      if (!id || !typeId) return;
      const responses = scope;
    const label =
        typeof data.label === "string"
          ? data.label
          : typeof element.name === "string"
            ? element.name
            : id;

      const rawEnvelope = id in responses ? responses[id] : null;
      let rawValue: unknown = null;
      let flagged = false;
      let score: number | null = null;
      if (rawEnvelope && typeof rawEnvelope === "object") {
        const env = rawEnvelope as Record<string, unknown>;
        rawValue = "value" in env ? env.value : null;
        flagged = !!env.flagged;
        if ("score" in env && typeof env.score === "number") score = env.score;
        else if ("score" in env && env.score != null && !Number.isNaN(Number(env.score)))
          score = Number(env.score);
      }
      if (isUnansweredColourBands(typeId, rawValue)) return;
      if (!flagged) flagged = isAnswerFlagged(rawValue, data, options);
      // A scorable rating or slider carries no snapshot: its number is the
      // score, so derive it (and the low-answer flag) from the element config.
      const scalar = scalarScoringConfig(typeId, data);
      if (scalar) {
        const scored = scoreScalarAnswer(scalar, rawValue);
        if (score === null) score = scored.score;
        if (scored.flagged) flagged = true;
      }

      const files = extractFiles(typeId, rawValue);
      const subLabel = typeof data.subLabel === "string" ? data.subLabel.trim() : "";
      const geo = boundGeo(rawValue, options.staticMapUrl);
      const picks = CHOICE_TYPES.has(typeId) ? resolvePicks(rawValue, data, options) : undefined;
      out.push({
        elementId: id,
        label: String(label),
        ...(subLabel ? { subLabel } : {}),
        typeId,
        formattedValue: formatAnswerForType(typeId, rawValue, data),
        raw: rawEnvelope,
        flagged,
        score,
        ...(picks ? { picks } : {}),
        files,
        filesAreImages: files ? IMAGE_FILE_TYPES.has(typeId) : undefined,
        ...(geo ? { geo } : {}),
        ...(options.withGroups ? { group: groupFor(ancestors) } : {}),
      });
    },
    {
      onEnter: ({ element, instance }) => {
        const typeId = String(element.typeId ?? "");
        const id = String(element.id ?? "");
        const data = (element.data && typeof element.data === "object" ? element.data : {}) as Record<string, unknown>;
        if (typeId === "page") pageOrdinals.set(id, (pageSeq += 1));
        if (isRepeatScope(element)) {
          if (!options.showSectionHeadings) return;
          if (instance === undefined) {
            const label = typeof data.label === "string" ? data.label : String(element.name || "Section");
            out.push({ kind: "section", elementId: id, label } as AnswerGroupMarker);
          } else {
            out.push({
              kind: "section",
              elementId: `${id}:${instance}`,
              label: `${repeatLabel(element, data)} ${instance + 1}`,
            } as AnswerGroupMarker);
          }
          return;
        }
        if (typeId === "page" && options.showPageHeadings) {
          const label = typeof data.name === "string" ? data.name : String(element.name || "Page");
          out.push({ kind: "page", elementId: id, label } as AnswerGroupMarker);
        } else if (typeId === "section" && options.showSectionHeadings) {
          const label = typeof data.label === "string" ? data.label : String(element.name || "Section");
          out.push({ kind: "section", elementId: id, label } as AnswerGroupMarker);
        }
      },
    },
  );
}

function filterAnswers(answers: AnswerListItem[], elementData: Record<string, unknown>): AnswerListItem[] {
  // Read marker visibility flags from element data
  const showPageHeadings = elementData.showPageHeadings !== false;
  const showSectionHeadings = elementData.showSectionHeadings !== false;
  const showHeadingElements = elementData.showHeadingElements !== false;

  // Parse mode for answer filtering
  const modeRaw = "mode" in elementData ? elementData.mode : "all";
  let mode: string;
  if (Array.isArray(modeRaw)) {
    mode = modeRaw[0] && typeof modeRaw[0] === "object" ? String((modeRaw[0] as any).value ?? "all") : "all";
  } else {
    mode = String(modeRaw);
  }

  // Filter helper for actual answers (not markers)
  const isImage = (a: AnswerRow) => IMAGE_FILE_TYPES.has(a.typeId);
  const answerPassesMode = (a: AnswerRow): boolean => {
    if (mode === "flagged") return a.flagged;
    if (mode === "unflagged") return !a.flagged;
    if (mode === "images") return isImage(a);
    if (mode === "non_images") return !isImage(a);
    return true; // "all"
  };

  const kept = answers.filter((item) => {
    if (isAnswerGroupMarker(item)) {
      // Filter markers based on visibility flags
      if (item.kind === "page") return showPageHeadings;
      if (item.kind === "section") return showSectionHeadings;
      if (item.kind === "heading") return showHeadingElements;
      return true;
    }
    // Apply mode filtering to actual answers
    return answerPassesMode(item as AnswerRow);
  });
  return pruneEmptyAnswerGroups(kept);
}

const MARKER_RANK: Record<string, number> = { heading: 1, section: 2, page: 3 };

/**
 * Drop page / section / heading markers that have no answers under them —
 * a "Flagged only" list shouldn't print the heading of a page whose answers
 * were all filtered out. A marker survives only if an answer appears before
 * the next marker of the same or a wider scope (a heading is closed by the
 * next heading, section or page; a page only by the next page).
 */
export function pruneEmptyAnswerGroups<T extends AnswerListItem>(items: T[]): T[] {
  const out: T[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!isAnswerGroupMarker(item)) {
      out.push(item);
      continue;
    }
    const rank = MARKER_RANK[item.kind] ?? 0;
    let hasAnswer = false;
    for (let j = i + 1; j < items.length; j++) {
      const next = items[j];
      if (isAnswerGroupMarker(next)) {
        if ((MARKER_RANK[next.kind] ?? 0) >= rank) break;
        continue;
      }
      hasAnswer = true;
      break;
    }
    if (hasAnswer) out.push(item);
  }
  return out;
}

function formatAnswerValue(value: unknown): string {
  if (value === null || value === "" || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number" || typeof value === "string") return String(value);

  if (Array.isArray(value)) {
    const allScalar = value.every((v) => isScalar(v));
    if (allScalar) return value.map((v) => String(v)).join(", ");

    const labels: string[] = [];
    let allMatched = true;
    for (const entry of value) {
      if (!entry || typeof entry !== "object") {
        allMatched = false;
        break;
      }
      const e = entry as Record<string, unknown>;
      if (isScalar(e.label) && e.label !== "") labels.push(String(e.label));
      else if (isScalar(e.name) && e.name !== "") labels.push(String(e.name));
      else if (isScalar(e.value) && e.value !== "") labels.push(String(e.value));
      else {
        allMatched = false;
        break;
      }
    }
    if (allMatched && labels.length) return labels.join(", ");
  }

  if (value && typeof value === "object") {
    const v = value as Record<string, unknown>;
    // Geo-tag: the place reverse-geocoded at submission, else the point.
    const geoPoint = geoValue(v);
    if (geoPoint) return geoPoint.place?.label || `${geoPoint.lat.toFixed(5)}, ${geoPoint.lng.toFixed(5)}`;
    if (isScalar(v.formatted) && String(v.formatted) !== "") return String(v.formatted);
    if (
      ("firstName" in v || "lastName" in v) &&
      (!("firstName" in v) || isScalar(v.firstName)) &&
      (!("lastName" in v) || isScalar(v.lastName))
    ) {
      const parts = [
        "firstName" in v ? String(v.firstName ?? "").trim() : "",
        "lastName" in v ? String(v.lastName ?? "").trim() : "",
      ].filter((s) => s.length);
      if (parts.length) return parts.join(" ");
    }
    if ("from" in v && "to" in v && isScalar(v.from) && isScalar(v.to)) {
      const from = String(v.from);
      const to = String(v.to);
      if (from !== "" && to !== "") return `${from} – ${to}`;
      if (from !== "" || to !== "") return from !== "" ? from : to;
    }
    if (Array.isArray(v.selected)) {
      const other = isScalar(v.other) ? String(v.other) : "";
      const rendered: string[] = [];
      for (const token of v.selected) {
        if (!isScalar(token)) continue;
        const t = String(token);
        if (t === "__OTHER__" && other !== "") rendered.push(other);
        else if (t !== "__OTHER__") rendered.push(t);
      }
      if (rendered.length) return rendered.join(", ");
    }
    if (isScalar(v.label)) return String(v.label);
    if (isScalar(v.name)) return String(v.name);
    if (isScalar(v.value)) return String(v.value);
  }

  try {
    return JSON.stringify(value) ?? "";
  } catch {
    return "";
  }
}

function buildEventList(result: FormOutputResultData): EventRow[] {
  const events: EventRow[] = [];
  const actor = resolveActor(result);

  if (result.createdAt) {
    events.push({ at: result.createdAt, type: "result.created", label: "Draft started", actor });
  }

  if (
    result.updatedAt &&
    result.createdAt &&
    Date.parse(result.updatedAt) > Date.parse(result.createdAt) &&
    (!result.submittedAt || Date.parse(result.updatedAt) < Date.parse(result.submittedAt))
  ) {
    events.push({ at: result.updatedAt, type: "result.saved", label: "Last autosave", actor });
  }

  if (result.submittedAt) {
    events.push({ at: result.submittedAt, type: "result.submitted", label: "Submitted", actor });
  }

  const flaggedCount = Number(result.flaggedAnswerCount || 0);
  if (flaggedCount > 0 && result.submittedAt) {
    events.push({
      at: result.submittedAt,
      type: "result.answer-flagged",
      label: `${flaggedCount} flagged answer${flaggedCount === 1 ? "" : "s"}`,
      actor: null,
    });
  }

  const status = result.signoffStatus;
  if (
    result.signedOffAt &&
    (status === SIGNOFF_SATISFACTORY || status === SIGNOFF_UNSATISFACTORY)
  ) {
    const isSatisfactory = status === SIGNOFF_SATISFACTORY;
    const verdictLabel =
      result.signoffOutcomeLabel?.trim() ||
      (isSatisfactory
        ? resolveSignoffAcceptLabel({ signoffAcceptLabel: result.signoffAcceptLabel ?? null })
        : resolveSignoffDeclineLabel({ signoffDeclineLabel: result.signoffDeclineLabel ?? null }));
    events.push({
      at: result.signedOffAt,
      type: isSatisfactory
        ? "result.signed-off-satisfactory"
        : "result.signed-off-unsatisfactory",
      label: `Signed off — ${verdictLabel}`,
      actor: result.signedOffByName,
    });
  }

  events.sort((a, b) => {
    const at = Date.parse(a.at);
    const bt = Date.parse(b.at);
    return at === bt ? 0 : at < bt ? -1 : 1;
  });
  return events;
}

const OTHER_VALUE = "__OTHER__";
const NONE_VALUE = "__NONE__";

/** A geo-tag value `{ lat, lng, place? }`, or null when the value isn't one. */
function geoValue(v: unknown): { lat: number; lng: number; place: GeoPlace | null } | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const lat = Number(o.lat);
  const lng = Number(o.lng);
  if (!("lat" in o) || !("lng" in o) || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  let place: GeoPlace | null = null;
  const p = o.place;
  if (p && typeof p === "object") {
    const pl = p as Record<string, unknown>;
    const str = (x: unknown) => (typeof x === "string" && x.trim() !== "" ? x.trim() : null);
    const suburb = str(pl.suburb);
    const state = str(pl.state);
    const country = str(pl.country);
    const label = str(pl.label) ?? [suburb, state, country].filter(Boolean).join(", ");
    if (label) place = { suburb, state, country, label };
  }
  return { lat, lng, place };
}

/** The bound geo payload for a geo-tag answer, with the host's static map when it has one. */
function boundGeo(
  rawValue: unknown,
  staticMapUrl?: (lat: number, lng: number) => string | null,
): BoundGeoAnswer | null {
  const g = geoValue(rawValue);
  if (!g) return null;
  return {
    lat: g.lat,
    lng: g.lng,
    place: g.place,
    mapImageUrl: staticMapUrl ? staticMapUrl(g.lat, g.lng) : null,
    mapsHref: `https://www.google.com/maps/search/?api=1&query=${g.lat},${g.lng}`,
  };
}

/** The picked entries of a choice answer: a bare array or `{ selected: [...] }`. */
/** Element types whose answer is a pick from a response set. */
const CHOICE_TYPES = new Set(["multi-choice", "dropdown-select"]);

/** The responses of the set an element answers from, or null when unknown. */
function responsesForElement(
  data: Record<string, unknown>,
  options: CollectAnswersOptions,
): unknown[] | null {
  const setId = data.responseSetId;
  if (setId == null || setId === "") return null;
  if (typeof setId === "string" && setId.startsWith("local_")) {
    for (const set of options.localSets ?? []) {
      if (set && typeof set === "object" && (set as Record<string, unknown>).id === setId) {
        const r = (set as Record<string, unknown>).responses;
        return Array.isArray(r) ? r : [];
      }
    }
    return null;
  }
  const g = options.globalSets?.[String(setId)];
  return g && Array.isArray(g.responses) ? g.responses : null;
}

function findResponse(responses: unknown[], needle: string): Record<string, unknown> | null {
  for (const r of responses) {
    if (r && typeof r === "object" && String((r as Record<string, unknown>).name ?? "") === needle) return r as Record<string, unknown>;
  }
  for (const r of responses) {
    if (r && typeof r === "object" && String((r as Record<string, unknown>).value ?? "") === needle) return r as Record<string, unknown>;
  }
  return null;
}

/**
 * The options a choice answer picked, each with its response colour so a
 * results view can show the answer the way the fill did. The label is the
 * pick's own (an Other entry keeps its typed text); the colour comes from
 * the response set, matched by name then value.
 */
function resolvePicks(
  rawValue: unknown,
  data: Record<string, unknown>,
  options: CollectAnswersOptions,
): BoundAnswerPick[] | undefined {
  const entries = pickEntries(rawValue);
  if (!entries.length) return undefined;
  const responses = responsesForElement(data, options);
  const picks: BoundAnswerPick[] = [];
  for (const entry of entries) {
    const value = pickValue(entry);
    const e = entry && typeof entry === "object" ? (entry as Record<string, unknown>) : null;
    const label =
      e && typeof e.label === "string" && e.label !== ""
        ? e.other === true && value
          ? `${e.label}: ${value}`
          : e.label
        : value ?? "";
    if (!label) continue;
    let colour: string | null = null;
    if (responses && value && value !== OTHER_VALUE && value !== NONE_VALUE && e?.other !== true && e?.none !== true) {
      const r = findResponse(responses, value);
      if (r && typeof r.colour === "string" && r.colour) colour = r.colour;
    }
    picks.push({ label, colour });
  }
  return picks.length ? picks : undefined;
}

function pickEntries(rawValue: unknown): unknown[] {
  if (Array.isArray(rawValue)) return rawValue;
  if (rawValue && typeof rawValue === "object") {
    const sel = (rawValue as Record<string, unknown>).selected;
    if (Array.isArray(sel)) return sel;
  }
  if (typeof rawValue === "string" && rawValue !== "") return [rawValue];
  return [];
}

/** A pick's canonical value: a string, or `{ value }` / `{ name }`. */
function pickValue(entry: unknown): string | null {
  if (typeof entry === "string") return entry !== "" ? entry : null;
  if (entry && typeof entry === "object") {
    const e = entry as Record<string, unknown>;
    if (typeof e.value === "string" && e.value !== "") return e.value;
    if (typeof e.name === "string" && e.name !== "") return e.name;
  }
  return null;
}

/**
 * Whether an answer is flagged, the way FormDocumentResultService counts
 * flagged answers: the pick's own snapshot when the fill client stored one
 * (`{ value, flagged }` entries), else the element's response set — a form-
 * local set from the answered version, or a global set the caller loaded —
 * matched by response name, then value. Other / None picks never flag.
 */
function isAnswerFlagged(
  rawValue: unknown,
  data: Record<string, unknown>,
  options: CollectAnswersOptions,
): boolean {
  const entries = pickEntries(rawValue);
  if (!entries.length) return false;

  // 1. Snapshot on the picks (current web + native fill clients).
  let sawSnapshot = false;
  for (const entry of entries) {
    if (entry && typeof entry === "object" && "flagged" in (entry as object)) {
      const f = (entry as Record<string, unknown>).flagged;
      if (typeof f === "boolean") {
        sawSnapshot = true;
        if (f) return true;
      }
    }
  }
  if (sawSnapshot) return false;

  // 2. Fall back to the response set the element uses.
  const setId = data.responseSetId;
  if (setId == null || setId === "") return false;
  let responses: unknown[] | null = null;
  if (typeof setId === "string" && setId.startsWith("local_")) {
    for (const set of options.localSets ?? []) {
      if (set && typeof set === "object" && (set as Record<string, unknown>).id === setId) {
        const r = (set as Record<string, unknown>).responses;
        responses = Array.isArray(r) ? r : [];
        break;
      }
    }
  } else {
    const g = options.globalSets?.[String(setId)];
    responses = g && Array.isArray(g.responses) ? g.responses : null;
  }
  if (!responses) return false;

  const find = (needle: string) => {
    for (const r of responses!) {
      if (r && typeof r === "object" && String((r as Record<string, unknown>).name ?? "") === needle) return r;
    }
    for (const r of responses!) {
      if (r && typeof r === "object" && String((r as Record<string, unknown>).value ?? "") === needle) return r;
    }
    return null;
  };
  for (const entry of entries) {
    const v = pickValue(entry);
    if (!v || v === OTHER_VALUE || v === NONE_VALUE) continue;
    const r = find(v) as Record<string, unknown> | null;
    if (r && !!r.flagged) return true;
  }
  return false;
}

/** Filler display name, degrading to the anonymous filler's email, then null. */
function resolveActor(result: FormOutputResultData): string | null {
  return result.submittedByName || result.fillerEmail || null;
}

function substitutePlaceholders(value: string, placeholders: Record<string, string>): string {
  if (!Object.keys(placeholders).length) return value;
  let out = value;
  for (const [needle, replacement] of Object.entries(placeholders)) {
    out = out.split(needle).join(replacement ?? "");
  }
  return out;
}

function isScalar(v: unknown): v is string | number | boolean {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean";
}

function safeJsonParse(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}
